import type { AgentTask } from '../types';
import { AUDIT_TYPE_LABELS } from './auditLabels';

export type UnifiedTaskStage = 'needs_attention' | 'in_progress' | 'done';
export type UnifiedTaskView = 'attention' | 'work' | 'history';
export type UnifiedWorkGroupKey = 'in_progress' | 'queued';

/**
 * One row on the practice Tasks page. Every row comes from the agent-suite task list, which returns
 * legacy Task rows and engine work (`task_<32hex>` ids, mapped server-side to the same shape).
 * `id` is the task id and the only identity: list keys, selection, and every action use it.
 */
export interface UnifiedTaskItem {
  id: string;
  title: string;
  /** The raw objective the agent was given; shown only in the collapsed "asked to do" disclosure. */
  objective?: string;
  searchText: string;
  /** Role key of the owning agent ('seo', 'ads'); null only when the server sent none. */
  agentType: string | null;
  typeLabel: string;
  stage: UnifiedTaskStage;
  status: AgentTask['state'];
  statusLabel: string;
  /** Last state change (epoch ms); drives "Updated today", the date label, and sort order. */
  lastUpdatedAt: number | null;
  task: AgentTask;
}

export interface UnifiedTaskFilterOptions {
  view: UnifiedTaskView;
  search?: string;
  updatedToday?: boolean;
  /** Keep only one agent's items; null/undefined = every agent. */
  agentType?: string | null;
  now: number;
}

export interface UnifiedTaskPage {
  items: UnifiedTaskItem[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface UnifiedWorkGroup {
  key: UnifiedWorkGroupKey;
  items: UnifiedTaskItem[];
}

// The audit labels come from one shared constant (`AUDIT_TYPE_LABELS`) so this map cannot drift
// from it or from the browser assertions that quote these strings verbatim.
const TYPE_LABELS: Record<string, string> = {
  access_request: 'Access request',
  ai_visibility_fix: 'AI visibility fix',
  blog_post_draft: 'Blog post',
  content_page_draft: 'Website content',
  gbp_listing_edit: 'Business profile update',
  gbp_post: 'GBP Post',
  gsc_content_gap: 'Search content',
  site_technical_fix: 'Website fix',
  site_page_edit: 'Website edit',
  task_generation: 'Task planning',
  practice_ad_strategy: 'Ad strategy',
  ad_access_tracking: 'Ad account access & tracking',
  ad_campaign_build: 'Campaign setup',
  ad_targeting: 'Ad targeting',
  ad_conversion_destination: 'Ad landing page',
  dental_meta_ad_creatives: 'Facebook & Instagram ads',
  google_search_creative: 'Google Search ads',
  ad_compliance_qa: 'Pre-launch compliance check',
  ad_launch_gate: 'Go-live approval',
  ad_campaign_change: 'Ad campaign change',
  // Community (Joe)
  community_scout: "Joe's picks",
  touch_draft: 'Note or invite to send',
  cadence_keeper: 'Reconnect with a partner',
  referral_checkin: 'Referral check-in',
  // Reviews (Betty)
  review_program_setup: 'Next step to start texting',
  front_desk_routine_coach: 'Front desk routine',
  send_timing_tune: 'When texts go out',
  invite_copy_tune: 'What the texts say',
  private_feedback_recovery: 'Call back an unhappy patient',
  ...AUDIT_TYPE_LABELS,
};

const STAGE: Record<AgentTask['state'], UnifiedTaskStage> = {
  ready: 'in_progress',
  blocked: 'needs_attention',
  awaiting_approval: 'needs_attention',
  in_progress: 'in_progress',
  done: 'done',
  cancelled: 'done',
  failed: 'needs_attention',
  needs_review: 'needs_attention',
};

const STATUS_LABEL: Record<AgentTask['state'], string> = {
  ready: 'Queued',
  blocked: 'Blocked',
  awaiting_approval: 'Needs your approval',
  in_progress: 'In progress',
  done: 'Done',
  cancelled: 'Cancelled',
  failed: 'Failed',
  needs_review: 'Needs your review',
};

/** Jenny's ad changes are draft-only: "approved" means recorded, so the list says that, not "Done". */
const AD_CHANGE_STATUS_LABEL: Partial<Record<AgentTask['state'], string>> = {
  done: 'Approved',
  cancelled: 'Rejected',
};

/** A skipped ad change is the owner passing on it, not rejecting it: the two stay apart in the list. */
const AD_CHANGE_SKIPPED_LABEL = 'Skipped';
/** Jenny's own "no campaign qualifies" close: neither the owner's reject nor a skip. */
const AD_CHANGE_CLOSED_LABEL = 'Closed — no change';
export const AD_CHANGE_COULD_NOT_DRAFT_LABEL = 'Closed — couldn’t draft, will retry next check';
/** The agency withdrew open work: it was out of date, or the change was made outside PracticeNova. */
export const WITHDRAWN_LABEL = {
  out_of_date: 'Withdrawn — out of date',
  done_outside: 'Done outside PracticeNova',
} as const;

/** Shown next to the agent's name while it answers the practice's message ("Sam · Replying to you"). */
const REPLYING_LABEL = 'Replying to you';
/** An approved setting change that did not apply (see settingNotApplied). */
const NOT_APPLIED_LABEL = 'Not applied';

const STAGE_RANK: Record<UnifiedTaskStage, number> = {
  needs_attention: 0,
  in_progress: 1,
  done: 2,
};

function finiteTimestamp(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function taskTypeLabel(skillKey: string): string {
  return TYPE_LABELS[skillKey] ?? 'Task';
}

/**
 * An approved Betty change that failed to apply to the texting program: the task is done, but the
 * practice still has to retry it (Try again in the task), so it belongs in Needs attention.
 */
function settingNotApplied(task: AgentTask): boolean {
  const publication = task.payload?.publication;
  return (
    task.state === 'done' &&
    publication?.channel === 'review_sms' &&
    publication.status === 'failed'
  );
}

function buildItem(task: AgentTask): UnifiedTaskItem {
  const notApplied = settingNotApplied(task);
  const typeLabel = taskTypeLabel(task.skillKey);
  // Ranked and proposed work carries the agent's own plain-English headline (server `ownerTitle`):
  // lead with it, so two "Search content" tasks read apart, with the type label under it. Every
  // other row (an audit, a report, a quota slot) keeps the type label as its headline and its raw
  // objective behind the collapsed "asked to do" disclosure.
  const headline = task.ownerTitle === true ? task.title.trim() : '';
  const title = headline && headline !== typeLabel ? headline : typeLabel;
  return {
    id: task.id,
    title,
    objective:
      title === typeLabel && task.title && task.title !== typeLabel ? task.title : undefined,
    searchText: `${task.title} ${typeLabel} ${task.skillKey}`.toLocaleLowerCase(),
    agentType: typeof task.agentType === 'string' && task.agentType ? task.agentType : null,
    typeLabel,
    // Failed work the practice has marked reviewed has nothing left to do: History.
    stage: notApplied
      ? 'needs_attention'
      : task.state === 'failed' && task.failureReviewed
        ? 'done'
        : STAGE[task.state],
    status: task.state,
    statusLabel: task.replying
      ? REPLYING_LABEL
      : notApplied
        ? NOT_APPLIED_LABEL
        : withdrawnLabel(task) ||
          (task.skillKey === 'ad_campaign_change' && adChangeLabel(task)) ||
          STATUS_LABEL[task.state],
    lastUpdatedAt: finiteTimestamp(task.lastAdvancedAt),
    task,
  };
}

function withdrawnLabel(task: AgentTask): string | undefined {
  const kind = task.payload?.cancelKind;
  return task.state === 'cancelled' && (kind === 'out_of_date' || kind === 'done_outside')
    ? WITHDRAWN_LABEL[kind]
    : undefined;
}

function adChangeLabel(task: AgentTask): string | undefined {
  if (task.state !== 'cancelled') return AD_CHANGE_STATUS_LABEL[task.state];
  const kind = task.payload?.cancelKind;
  if (kind === 'skipped' || kind === 'cancelled') return AD_CHANGE_SKIPPED_LABEL;
  if (kind === 'no_change')
    return task.payload?.couldNotDraft ? AD_CHANGE_COULD_NOT_DRAFT_LABEL : AD_CHANGE_CLOSED_LABEL;
  return AD_CHANGE_STATUS_LABEL.cancelled;
}

/** One row per task. Engine and legacy rows are built the same way. */
export function buildUnifiedTaskItems(tasks: readonly AgentTask[]): UnifiedTaskItem[] {
  return tasks.map(buildItem);
}

export function belongsToView(item: UnifiedTaskItem, view: UnifiedTaskView): boolean {
  if (view === 'attention') return item.stage === 'needs_attention';
  if (view === 'history') return item.stage === 'done';
  return item.stage === 'in_progress';
}

export function isUpdatedToday(item: UnifiedTaskItem, now: number): boolean {
  if (item.lastUpdatedAt === null) return false;
  const updated = new Date(item.lastUpdatedAt);
  const today = new Date(now);
  if (Number.isNaN(updated.getTime()) || Number.isNaN(today.getTime())) return false;
  return (
    updated.getFullYear() === today.getFullYear() &&
    updated.getMonth() === today.getMonth() &&
    updated.getDate() === today.getDate()
  );
}

/** Search applies across all stages when text exists, so a known title stays discoverable. */
export function filterUnifiedTaskItems(
  items: UnifiedTaskItem[],
  options: UnifiedTaskFilterOptions,
): UnifiedTaskItem[] {
  const query = options.search?.trim().toLocaleLowerCase() ?? '';
  return sortUnifiedTaskItems(
    items.filter((item) => {
      const matchesSearch = !query || item.searchText.includes(query);
      const matchesView = query ? true : belongsToView(item, options.view);
      const matchesToday = !options.updatedToday || isUpdatedToday(item, options.now);
      const matchesAgent = !options.agentType || item.agentType === options.agentType;
      return matchesSearch && matchesView && matchesToday && matchesAgent;
    }),
  );
}

/** Items belonging to one agent, or all of them when no agent is chosen. */
export function filterByAgent(
  items: UnifiedTaskItem[],
  agentType: string | null | undefined,
): UnifiedTaskItem[] {
  return agentType ? items.filter((item) => item.agentType === agentType) : items;
}

export function countUnifiedTaskViews(items: UnifiedTaskItem[]): Record<UnifiedTaskView, number> {
  return {
    attention: items.filter((item) => belongsToView(item, 'attention')).length,
    work: items.filter((item) => belongsToView(item, 'work')).length,
    history: items.filter((item) => belongsToView(item, 'history')).length,
  };
}

/** Stage first, newest change next, id last. Stable and non-mutating. */
export function sortUnifiedTaskItems(items: UnifiedTaskItem[]): UnifiedTaskItem[] {
  return [...items].sort((left, right) => {
    const stageDifference = STAGE_RANK[left.stage] - STAGE_RANK[right.stage];
    if (stageDifference !== 0) return stageDifference;
    const updateDifference = (right.lastUpdatedAt ?? 0) - (left.lastUpdatedAt ?? 0);
    if (updateDifference !== 0) return updateDifference;
    return left.id.localeCompare(right.id);
  });
}

/** The work view has one group for work in progress (task rows have no future due dates to
 *  spread across This week / Later): the in-progress stage holds both what the agent is on right
 *  now and what is waiting its turn, so only the first is "In progress" and the rest is "Queued". */
export function groupUnifiedWorkItems(items: UnifiedTaskItem[]): UnifiedWorkGroup[] {
  const inProgress = items.filter((item) => item.stage === 'in_progress');
  const groups: Record<UnifiedWorkGroupKey, UnifiedTaskItem[]> = {
    in_progress: inProgress.filter((item) => item.status === 'in_progress'),
    queued: inProgress.filter((item) => item.status !== 'in_progress'),
  };
  return (['in_progress', 'queued'] as const)
    .map((key) => ({ key, items: groups[key] }))
    .filter((group) => group.items.length > 0);
}

export function paginateUnifiedTaskItems(
  items: UnifiedTaskItem[],
  page: number,
  pageSize = 20,
): UnifiedTaskPage {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 20;
  const end = safePage * safePageSize;
  return {
    items: items.slice(0, end),
    page: safePage,
    pageSize: safePageSize,
    total: items.length,
    hasMore: end < items.length,
  };
}
